// File: src/components/EndOfEventFeedbackModal.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import axios from 'axios';

/* -------------------- UI tokens -------------------- */
const COLORS = {
  bg: '#ffffff',
  text: '#0b0b0b',
  subtext: '#525252',
  border: '#EEF0F2',
  brand: '#0b0b0b',
  overlay: 'rgba(0,0,0,0.4)',
  chipBg: '#F4F4F5',
  pillBg: '#EAF2FF',
  pillText: '#1E40AF',
};

/* Fixed sizes for a comfy keyboard mode */
const TYPING_SHEET_HEIGHT = '40dvh';   // fixed size while typing
const NORMAL_SHEET_MAX_H  = '78dvh';   // cap in normal mode
const NORMAL_SHEET_MIN_H  = 500;       // px

function isPWA() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

export default function EndOfEventFeedbackModal({
  open,
  onClose,
  eventId,
  userId,
  onLeaveEvent,
}) {
  const sheetRef   = useRef(null);
  const headerRef  = useRef(null);
  const footerRef  = useRef(null);
  const textRef    = useRef(null);

  const [rating, setRating] = useState(null); // 1..4
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [typing, setTyping] = useState(false);

  // visual viewport snapshot (for keyboard offset)
  const [vv, setVv] = useState({
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
    offsetTop: 0,
  });

  const key = useMemo(() => `feedback_given_event_${eventId}`, [eventId]);
  const canSubmit = Boolean(rating) && !submitting;

  /* ───────── Freeze background scrolling hard ───────── */
  useEffect(() => {
    if (!open) return;

    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta?.content;

    const prev = {
      top: document.body.style.top,
      pos: document.body.style.position,
      wid: document.body.style.width,
      ovf: document.body.style.overflow,
    };
    const y = window.scrollY || window.pageYOffset;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    document.documentElement.style.overscrollBehavior = 'none';
    if (meta) meta.content = '#999998';

    return () => {
      document.body.style.position = prev.pos;
      document.body.style.top = prev.top;
      document.body.style.width = prev.wid;
      document.body.style.overflow = prev.ovf;
      window.scrollTo(0, y);
      document.documentElement.style.overscrollBehavior = '';
      if (meta) meta.content = typeof prevTheme === 'string' ? prevTheme : '#ffffff';
    };
  }, [open]);

  /* ───────── Track visual viewport to pin sheet above keyboard ───────── */
  useEffect(() => {
    if (!open) return;
    const onVV = () => {
      const v = window.visualViewport;
      if (!v) return;
      setVv({ height: v.height, offsetTop: v.offsetTop || 0 });
    };
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onVV);
      window.visualViewport.addEventListener('scroll', onVV);
      onVV();
      return () => {
        window.visualViewport.removeEventListener('resize', onVV);
        window.visualViewport.removeEventListener('scroll', onVV);
      };
    }
  }, [open]);

  if (!open) return null;

  // 4-point scale (no neutral)
  const emojis = [
    { val: 4, label: 'Great',    icon: '😊' },
    { val: 3, label: 'Good',     icon: '🙂' },
    { val: 2, label: 'Bad',      icon: '🙁' },
    { val: 1, label: 'Terrible', icon: '😞' },
  ];

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/feedback`,
        {
          userId,
          eventId: String(eventId || '').replace(/^mixer-/, ''),
          feedbackType: 'event_experience',
          rating: Number(rating),
          feedbackMessage: text?.trim() || '',
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}` } }
      );
      try { localStorage.setItem(key, '1'); } catch {}
      setDone(true);
    } catch (err) {
      console.error('[EndOfEventFeedbackModal] submit error', err);
      alert('Sorry, we could not save your feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // Compute gap between layout viewport and visual viewport (keyboard height)
  const layoutH = typeof window !== 'undefined' ? window.innerHeight : vv.height;
  const kbGap = Math.max(0, layoutH - (vv.offsetTop + vv.height));

  // prevent background scroll/gentures on backdrop itself
  const stopScroll = (e) => {
    if (sheetRef.current && sheetRef.current.contains(e.target)) return;
    e.preventDefault();
  };

  // Compute fixed heights for content when typing vs normal
  const SHEET_STYLE = typing
    ? {
        height: TYPING_SHEET_HEIGHT,        // fixed size above keyboard
        maxHeight: TYPING_SHEET_HEIGHT,
        minHeight: '360px',
      }
    : {
        maxHeight: NORMAL_SHEET_MAX_H,      // allow it to be roomy but capped
        minHeight: `${NORMAL_SHEET_MIN_H}px`,
      };

  // While typing we hide the footer (more room for the textarea),
  // and we show only the textarea (emojis/title hidden).
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Event feedback"
      style={{
        ...styles.backdrop,
        paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${Math.round(kbGap)}px)`,
      }}
      onWheel={stopScroll}
      onTouchMove={stopScroll}
    >
      <div
        ref={sheetRef}
        style={{
          ...styles.sheet,
          ...SHEET_STYLE,
          marginBottom: `calc(${isPWA() ? 32 : 12}px + env(safe-area-inset-bottom, 0px))`,          transition: 'height .25s ease, max-height .25s ease',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div ref={headerRef} style={styles.header}>
          <div style={styles.headerTitle}>Rate your experience</div>
        </div>

        {/* Content (scrolls internally if needed) */}
        <div style={styles.content}>
          {!done ? (
            <>
              {!typing && (
                <>
                  <div style={styles.centeredBlock}>
                    <div style={styles.h1}>How was your experience?</div>
                    <div style={styles.h2}>
                      Your review will help us improve our product and make it
                      more user-friendly.
                    </div>
                  </div>

                  <div style={styles.emojiRow}>
                    {emojis.map((e) => {
                      const selected = rating === e.val;
                      return (
                        <button
                          key={e.val}
                          onClick={() => setRating(e.val)}
                          aria-pressed={selected}
                          aria-label={e.label}
                          title={e.label}
                          style={styles.emojiTap}
                        >
                          <span
                            style={{
                              ...styles.emojiCircle,
                              ...(selected ? styles.emojiCircleActive : {}),
                            }}
                          >
                            <span
                              style={{
                                ...styles.emojiIcon,
                                ...(selected ? {} : styles.emojiIconMuted),
                              }}
                            >
                              {e.icon}
                            </span>
                          </span>
                          <span
                            style={{
                              ...styles.pillLabel,
                              visibility: selected ? 'visible' : 'hidden',
                            }}
                          >
                            {e.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              <textarea
                ref={textRef}
                placeholder="Share feedback… (optional)"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={typing ? 8 : 5}
                onFocus={() => {
                  setTyping(true);
                  setTimeout(() => {
                    try { textRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch {}
                  }, 120);
                }}
                onBlur={() => setTyping(false)}
                style={{
                  ...styles.textarea,
                     flexGrow: 1,              // let it stretch to fill vertical space
   alignSelf: "stretch",
   marginTop: typing ? 0 : 0, // less top gap
   height: "100%",      
                  ...(typing
                    ? {
                        marginTop: 8,
                        height: '280px',
                        maxHeight: '280px',
                      }
                    : {}),
                }}
              />
            </>
          ) : (
            <>
              <div style={styles.centeredBlock}>
                <div style={styles.h1}>Thank you! 🎉</div>
                <div style={styles.h2}>We appreciate your feedback.</div>
              </div>
            </>
          )}
        </div>

        {/* Footer (hidden while typing to keep the sheet short) */}
        <div
          ref={footerRef}
          style={{ ...styles.footer, ...(typing ? styles.hidden : null) }}
        >
          {!done ? (
            <>
              <button type="button" style={styles.secondaryBtn} onClick={onClose}>
                Not This Time
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                style={{
                  ...styles.primaryBtn,
                  ...(canSubmit ? {} : styles.primaryBtnDisabled),
                }}
              >
                {submitting ? 'Submitting…' : 'Submit Review'}
              </button>
            </>
          ) : (
            <>
              <button type="button" style={styles.secondaryBtn} onClick={onClose}>
                Stay Here
              </button>
              <button
                type="button"
                onClick={() => {
                  onClose?.();
                  onLeaveEvent?.();
                }}
                style={styles.primaryBtn}
              >
                Leave Event
              </button>
            </>
          )}
        </div>

        <style>
          {`
            @keyframes sheetEnter {
              from { transform: translateY(16px); opacity: 0; }
              to   { transform: translateY(0);    opacity: 1; }
            }
            @media (prefers-reduced-motion: reduce) {
              * { animation: none !important; transition: none !important; }
            }
            button:focus-visible {
              outline: 0;
              box-shadow: 0 0 0 3px rgba(59,130,246,.45);
            }
          `}
        </style>
      </div>
    </div>
  );
}

/* -------------------- Styles -------------------- */
const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: COLORS.overlay,
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    overscrollBehavior: 'contain',
    overflow: 'hidden',
  },
  sheet: {
    width: '100%',
    maxWidth: 520,
    margin: '0 10px',
    backgroundColor: COLORS.bg,
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'sheetEnter .25s ease-out',
    border: `1px solid ${COLORS.border}`,
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    height: 48,
    padding: '0 16px',
    borderBottom: `1px solid ${COLORS.border}`,
    background: '#fff',
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: COLORS.text,
    letterSpacing: '-0.01em',
  },

  content: {
    padding: '16px',
    background: '#fff',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto', // internal scroll when needed
    WebkitOverflowScrolling: 'touch',
  },

  centeredBlock: {
    textAlign: 'center',
    margin: '12px 0 10px',
  },
  h1: {
    fontSize: 24,
    fontWeight: 900,
    color: COLORS.text,
    margin: 0,
  },
  h2: {
    fontSize: 13.5,
    color: COLORS.subtext,
    lineHeight: 1.5,
    marginTop: 6,
    marginRight: 12,
    marginLeft: 12,
  },

  emojiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 14,
    marginTop: 14,
    marginBottom: 14,
    justifyItems: 'center',
  },
  emojiTap: {
    appearance: 'none',
    WebkitAppearance: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'center',
  },
  emojiCircle: {
    width: 76,
    height: 76,
    borderRadius: 999,
    display: 'grid',
    placeItems: 'center',
    background: 'transparent',
    border: '1px solid transparent',
    transition: 'background 120ms ease, border-color 120ms ease, transform 80ms ease',
  },
  emojiCircleActive: {
    background: '#F4F4F5',
    border: `1px solid ${COLORS.border}`,
    transform: 'translateY(-1px)',
  },
  emojiIcon: {
    fontSize: 60,
    lineHeight: 1,
  },
  emojiIconMuted: {
    opacity: 0.45,
  },
  pillLabel: {
    display: 'inline-block',
    marginTop: 4,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 800,
    borderRadius: 999,
    background: COLORS.pillBg,
    color: COLORS.pillText,
    minHeight: 8,
  },

  textarea: {
    width: '100%',
    marginTop: 'auto',
    padding: 12,
    borderRadius: 10,
    border: `1px solid ${COLORS.border}`,
    background: '#fff',
    fontSize: 13.5,
    color: COLORS.text,
    resize: 'none',
    minHeight: 125,
    boxSizing: 'border-box',
    fontFamily: "'Open Sans', sans-serif",
    transition: 'height 220ms ease',
  },

  footer: {
    display: 'flex',
    gap: 10,
    padding: 16,
    borderTop: `1px solid ${COLORS.border}`,
    background: '#fff',
  },
  hidden: {
    display: 'none',
  },
  secondaryBtn: {
    flex: 1,
    borderRadius: 10,
    border: `1px solid ${COLORS.text}`,
    background: '#fff',
    color: COLORS.text,
    fontWeight: 800,
    fontSize: 12,
    padding: '12px 0',
    cursor: 'pointer',
  },
  primaryBtn: {
    flex: 1,
    borderRadius: 10,
    border: '1px solid transparent',
    background: COLORS.brand,
    color: '#fff',
    fontWeight: 800,
    fontSize: 12,
    padding: '12px 0',
    cursor: 'pointer',
  },
  primaryBtnDisabled: {
    opacity: 0.65,
    cursor: 'not-allowed',
  },
};
